import { Card, CardContent } from "@mui/material"
import React, { MouseEventHandler, ReactElement } from "react"
import styled from "styled-components"
import { theme } from "../../../theme"
import { Button, ButtonVariant } from "../../atoms/button"
import { TextDisclaimer, TextRegular } from "../../atoms/text"
import { Profile, ProfileProps } from "../../molecules/profile"
import { Attachment, AttachmentProps } from "../../molecules/attachment"

export type CommentProps = {
  comment: string
  profile: ProfileProps
  date?: string
  className?: string
  dataCy?: string
  attachments?: AttachmentProps[]
  actions?: {
    label: string
    icon?: ReactElement
    buttonVariant?: ButtonVariant
    onClick?: MouseEventHandler<HTMLElement>
  }[]
  onClickAttachment?: (id: string) => void
}

export const Comment = (props: CommentProps) => {
  const { date, comment, profile, className, dataCy, attachments, actions, onClickAttachment } = props
  const hasHeader = !!date || !!actions

  return (
    <Root elevation={0} $hasHeader={hasHeader} className={className} data-cy={dataCy}>
      {hasHeader && (
        <CardHeader>
          {date && <TextDisclaimer>{date}</TextDisclaimer>}
          {actions && (
            <Actions>
              {actions.map((action) => (
                <Button
                  variant={action.buttonVariant ?? ButtonVariant.text}
                  onClick={action.onClick}
                  icon={action.icon}
                >
                  {action.label}
                </Button>
              ))}
            </Actions>
          )}
        </CardHeader>
      )}
      <ProfileWrapper>
        <Profile {...profile} />
      </ProfileWrapper>
      <CardContent>
        <TextRegular>{comment}</TextRegular>
        {attachments && (
          <Attachments>
            {attachments.map((attachment) => (
              <Attachment
                key={attachment.id}
                id={attachment.id}
                name={attachment.name}
                size={attachment.size}
                onClick={onClickAttachment}
              />
            ))}
          </Attachments>
        )}
      </CardContent>
    </Root>
  )
}

const Root = styled(Card)<{ $hasHeader: boolean }>`
  background-color: ${theme.color.white2};
  padding: ${(p) => (p.$hasHeader === true ? "0" : theme.spacing.l)} ${theme.spacing.m} 0;
`

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  padding: ${theme.spacing.l} ${theme.spacing.l} 0;
  min-height: 56px;
`

const Actions = styled.div`
  display: grid;
  grid-gap: ${theme.spacing.m};
  grid-auto-flow: column;
  margin-left: auto;
`

const ProfileWrapper = styled.div`
  margin-left: ${theme.spacing.m};
`

const Attachments = styled.div`
  display: grid;
  grid-gap: ${theme.spacing.m};
  margin-top: ${theme.spacing.l};
`
